

import { useState, useEffect, useCallback } from 'react';
import { Technique } from '@/types/technique';
import { generateShareableUrl } from '@/services/sharing/techniqueSharing';
import { toast } from 'sonner';
import { useReferralCodeSimple } from '@/hooks/affiliates/useReferralCodeSimple';

interface TechniqueSharingReturn {
  shareUrl: string | null;
  copied: boolean;
  isLoading: boolean;
  error: string | null;
  errorDetails?: unknown;
  generateShareLink: () => Promise<void>;
  copyToClipboard: () => void;
}

export const useTechniqueSharing = (technique: Technique | null, isOpen: boolean): TechniqueSharingReturn => {
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<unknown>(null);
  const { referralCode } = useReferralCodeSimple();

  const generateShareLink = useCallback(async () => {
    if (!technique) return;
    
    setIsLoading(true);
    setError(null);
    setErrorDetails(null);
    
    try {
      // Generate a simple shareable URL using the centralized domain utility system
      const shareId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Use the centralized URL generation function with referral code from hook
      const shareableUrl = generateShareableUrl(shareId, referralCode || undefined);
      
      setShareUrl(shareableUrl);
      toast.success('Shareable link generated!');
    } catch (err: unknown) {
      console.error('Error generating shareable link:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setErrorDetails(err);
      toast.error('Failed to generate shareable link.');
    } finally {
      setIsLoading(false);
    }
  }, [technique, referralCode]);

  const copyToClipboard = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Generate the shareable URL when dialog opens and technique is available
  useEffect(() => {
    if (isOpen && technique && !shareUrl && !isLoading) {
      generateShareLink();
    }
  }, [isOpen, technique, shareUrl, isLoading, generateShareLink]);

  return { 
    shareUrl, 
    copied, 
    isLoading, 
    error, 
    errorDetails, 
    generateShareLink, 
    copyToClipboard 
  };
};

